import logger from "../utils/logger";
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Users, DollarSign, Calendar, Clock, TrendingUp, AlertCircle, CheckCircle,
    Wallet, FolderKanban, ListTodo, Package, MapPin, Target, Receipt,
    ClipboardList, Bell, ChevronRight, Star, FileSpreadsheet
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import API_URL from '../utils/api';
const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const StatCard = ({ label, value, sublabel, icon: Icon, iconBg, iconColor, testId, onClick, accent = 'blue' }) => {
    const accents = {
        blue:   { stripe: 'bg-gradient-to-r from-[#2563eb] to-[#3b82f6]', tint: 'from-blue-50/60',     ring: 'group-hover:ring-blue-200' },
        green:  { stripe: 'bg-gradient-to-r from-emerald-500 to-teal-500', tint: 'from-emerald-50/60',  ring: 'group-hover:ring-emerald-200' },
        amber:  { stripe: 'bg-gradient-to-r from-amber-500 to-orange-500',  tint: 'from-amber-50/60',   ring: 'group-hover:ring-amber-200' },
        red:    { stripe: 'bg-gradient-to-r from-rose-500 to-red-500',      tint: 'from-rose-50/60',    ring: 'group-hover:ring-rose-200' },
        purple: { stripe: 'bg-gradient-to-r from-violet-500 to-fuchsia-500', tint: 'from-violet-50/60', ring: 'group-hover:ring-violet-200' },
        cyan:   { stripe: 'bg-gradient-to-r from-cyan-500 to-sky-500',      tint: 'from-cyan-50/60',    ring: 'group-hover:ring-cyan-200' },
        indigo: { stripe: 'bg-gradient-to-r from-indigo-500 to-blue-500',   tint: 'from-indigo-50/60',  ring: 'group-hover:ring-indigo-200' },
        rose:   { stripe: 'bg-gradient-to-r from-pink-500 to-rose-500',     tint: 'from-pink-50/60',    ring: 'group-hover:ring-pink-200' },
        teal:   { stripe: 'bg-gradient-to-r from-teal-500 to-cyan-500',     tint: 'from-teal-50/60',    ring: 'group-hover:ring-teal-200' },
    };
    const a = accents[accent] || accents.blue;
    return (
        <Card className={`group relative rounded-xl border border-slate-200 bg-gradient-to-br ${a.tint} to-white overflow-hidden ${onClick ? 'cursor-pointer hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200' : 'transition-all'}`} data-testid={testId} onClick={onClick}>
            <div className={`absolute top-0 left-0 h-1 w-full ${a.stripe}`} />
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
                        <p className="text-3xl font-black text-slate-900 mt-1.5 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</p>
                        {sublabel && <p className="text-[11px] text-slate-500 mt-1.5">{sublabel}</p>}
                    </div>
                    <div className={`flex-shrink-0 p-2.5 rounded-lg ${iconBg}`}><Icon className={`h-5 w-5 ${iconColor}`} /></div>
                </div>
            </CardContent>
        </Card>
    );
};

const QuickLink = ({ icon: Icon, label, href, count, color }) => {
    const navigate = useNavigate();
    return (
        <button onClick={() => navigate(href)} className="flex items-center gap-3 p-3.5 bg-white border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all text-left w-full group" data-testid={`quick-link-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
            <span className="text-sm font-bold text-slate-800 flex-1">{label}</span>
            {count > 0 && <Badge className="rounded-full bg-red-100 text-red-700 text-[10px] font-bold">{count}</Badge>}
            <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
        </button>
    );
};

const Dashboard = () => {
    const { user, canManage } = useAuth();
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [myData, setMyData] = useState(null);
    const [mgrData, setMgrData] = useState(null);
    const [todayAttendance, setTodayAttendance] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            const requests = [
                axios.get(`${API_URL}/api/attendance/today`, { withCredentials: true }),
                axios.get(`${API_URL}/api/dashboard/my`, { withCredentials: true }),
            ];
            if (canManage) {
                requests.push(axios.get(`${API_URL}/api/analytics/dashboard`, { withCredentials: true }));
                requests.push(axios.get(`${API_URL}/api/dashboard/manager`, { withCredentials: true }));
            }
            const results = await Promise.all(requests);
            setTodayAttendance(results[0].data);
            setMyData(results[1].data);
            if (results[2]) setAnalytics(results[2].data);
            if (results[3]) setMgrData(results[3].data);
        } catch (error) {
            logger.error('Dashboard fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [canManage]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCheckIn = async () => {
        try { await axios.post(`${API_URL}/api/attendance/check-in`, {}, { withCredentials: true }); fetchData(); }
        catch (error) { alert(error.response?.data?.detail || 'Check-in failed'); }
    };
    const handleCheckOut = async () => {
        try { await axios.post(`${API_URL}/api/attendance/check-out`, {}, { withCredentials: true }); fetchData(); }
        catch (error) { alert(error.response?.data?.detail || 'Check-out failed'); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading dashboard...</div></div>;

    const isEmployee = user?.role === 'employee';
    const isManager = user?.role === 'manager';

    return (
        <div className="space-y-6" data-testid="dashboard">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {canManage ? 'Admin Dashboard' : isManager ? 'Manager Dashboard' : 'My Dashboard'}
                    </h1>
                    <p className="text-slate-500 mt-1">Welcome back, {user?.name}</p>
                </div>
                {myData?.unread_notifications > 0 && (
                    <Badge className="rounded-none bg-red-100 text-red-700 flex items-center gap-1 px-3 py-1.5">
                        <Bell className="h-3.5 w-3.5" />{myData.unread_notifications} unread
                    </Badge>
                )}
            </div>

            {/* Self-Onboarding Banner - Employee, incomplete */}
            {isEmployee && user?.onboarding_completed === false && (
                <div className="rounded-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 p-[2px] shadow-lg" data-testid="onboarding-banner">
                    <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-white p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-black text-slate-900 text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>Complete your onboarding</p>
                            <p className="text-sm text-slate-700 mt-0.5">Upload your Aadhaar, PAN, and Bank details so HR can verify your account. Takes 5 minutes.</p>
                        </div>
                        <button
                            onClick={() => navigate('/self-onboarding')}
                            className="flex-shrink-0 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm px-5 py-2.5 rounded-lg flex items-center gap-2 transition-all hover:shadow-md"
                            data-testid="onboarding-banner-cta"
                        >
                            Continue <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Attendance Widget - All Roles */}
            <Card className="rounded-none border-slate-200 border-l-4 border-l-brand" data-testid="attendance-widget">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-brand" />
                            {todayAttendance?.check_in ? (
                                <div>
                                    <span className="text-sm text-slate-700">Checked in at <strong>{new Date(todayAttendance.check_in).toLocaleTimeString()}</strong></span>
                                    {todayAttendance?.check_out && <span className="text-sm text-slate-500 ml-3">Out: {new Date(todayAttendance.check_out).toLocaleTimeString()}</span>}
                                    {todayAttendance?.hours_worked && <span className="text-sm text-slate-500 ml-3">{todayAttendance.hours_worked} hrs</span>}
                                </div>
                            ) : (
                                <span className="text-sm text-amber-600 font-medium">You haven't checked in today</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleCheckIn} disabled={todayAttendance?.check_in} className="bg-brand hover:bg-brand rounded-none h-8" data-testid="check-in-btn">Check In</Button>
                            <Button size="sm" onClick={handleCheckOut} disabled={!todayAttendance?.check_in || todayAttendance?.check_out} variant="outline" className="rounded-none h-8" data-testid="check-out-btn">Check Out</Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ===== EMPLOYEE VIEW ===== */}
            {isEmployee && myData && (
                <>
                    {/* My Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <StatCard label="My Tasks" value={myData.tasks.pending} sublabel={`${myData.tasks.overdue} overdue`} icon={ListTodo} iconBg="bg-blue-100" iconColor="text-brand" testId="stat-my-tasks" onClick={() => navigate('/tasks')} accent="blue" />
                        <StatCard label="Pending Leaves" value={myData.leaves.pending} sublabel={`${myData.leaves.approved} approved`} icon={Calendar} iconBg="bg-green-100" iconColor="text-green-600" testId="stat-my-leaves" onClick={() => navigate('/leaves')} accent="green" />
                        <StatCard label="Expense Claims" value={myData.expenses.pending} sublabel={`Rs.${(myData.expenses.approved_total || 0).toLocaleString()} approved`} icon={Receipt} iconBg="bg-amber-100" iconColor="text-amber-600" testId="stat-my-expenses" onClick={() => navigate('/expenses')} accent="amber" />
                        <StatCard label="Goals Progress" value={`${myData.goals.completed}/${myData.goals.total}`} sublabel="completed" icon={Target} iconBg="bg-purple-100" iconColor="text-purple-600" testId="stat-my-goals" onClick={() => navigate('/performance')} accent="purple" />
                    </div>

                    {/* Active Goals */}
                    {myData.goals.active?.length > 0 && (
                        <Card className="rounded-none border-slate-200">
                            <CardHeader className="border-b border-slate-200 py-3 px-4">
                                <CardTitle className="text-sm font-bold flex items-center gap-2"><Target className="h-4 w-4 text-purple-600" />Active Goals</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {myData.goals.active.map(g => (
                                    <div key={g._id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
                                        <div className="flex-1"><p className="text-sm font-medium text-slate-900">{g.title}</p></div>
                                        <div className="w-24 h-1.5 bg-slate-200 rounded-full"><div className="h-full bg-brand rounded-full" style={{ width: `${g.progress}%` }} /></div>
                                        <span className="text-xs font-bold text-slate-600 w-10 text-right">{g.progress}%</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Onboarding Progress */}
                    {myData.onboarding && (
                        <Card className="rounded-none border-slate-200 border-l-4 border-l-teal-500">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ClipboardList className="h-5 w-5 text-teal-600" />
                                        <div>
                                            <p className="text-sm font-bold text-slate-900">Onboarding: {myData.onboarding.template_name}</p>
                                            <p className="text-xs text-slate-500">{myData.onboarding.checklist?.filter(c => c.completed).length}/{myData.onboarding.checklist?.length} tasks completed</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 h-2 bg-slate-200 rounded-full"><div className="h-full bg-teal-600 rounded-full" style={{ width: `${myData.onboarding.progress}%` }} /></div>
                                        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => navigate('/onboarding')}>View</Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recent Tasks */}
                    {myData.tasks.recent?.length > 0 && (
                        <Card className="rounded-none border-slate-200">
                            <CardHeader className="border-b border-slate-200 py-3 px-4">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm font-bold flex items-center gap-2"><ListTodo className="h-4 w-4 text-brand" />Recent Tasks</CardTitle>
                                    <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => navigate('/tasks')}>View All</Button>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                {myData.tasks.recent.map(t => (
                                    <div key={t._id} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-slate-900 truncate">{t.title}</p>
                                            <p className="text-[10px] text-slate-400">{t.task_code}</p>
                                        </div>
                                        <Badge className={`rounded-none text-[10px] ${t.status === 'completed' ? 'bg-green-100 text-green-800' : t.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>{t.status?.replace('_', ' ')}</Badge>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Quick Links */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <QuickLink icon={MapPin} label="Geo Tracking" href="/geo-tracking" color="bg-blue-100" />
                        <QuickLink icon={Calendar} label="Leave Calendar" href="/leaves" color="bg-green-100" />
                        <QuickLink icon={DollarSign} label="My Payslips" href="/my-payslips" color="bg-emerald-100" />
                        <QuickLink icon={MapPin} label="Field Visits" href="/visits" count={myData.visits_today} color="bg-orange-100" />
                    </div>
                </>
            )}

            {/* ===== MANAGER / HR / ADMIN VIEW ===== */}
            {canManage && mgrData && analytics && (
                <>
                    {/* Pending Approvals Banner */}
                    {(mgrData.approvals.pending_leaves > 0 || mgrData.approvals.pending_expenses > 0) && (
                        <Card className="rounded-none border-slate-200 border-l-4 border-l-amber-500 bg-amber-50/30" data-testid="approvals-banner">
                            <CardContent className="p-4">
                                <div className="flex flex-wrap items-center gap-4">
                                    <AlertCircle className="h-5 w-5 text-amber-600" />
                                    <span className="text-sm font-medium text-slate-800">Pending Approvals:</span>
                                    {mgrData.approvals.pending_leaves > 0 && (
                                        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-amber-300" onClick={() => navigate('/leaves')} data-testid="approve-leaves-link">
                                            {mgrData.approvals.pending_leaves} Leave Requests
                                        </Button>
                                    )}
                                    {mgrData.approvals.pending_expenses > 0 && (
                                        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-amber-300" onClick={() => navigate('/expenses')} data-testid="approve-expenses-link">
                                            {mgrData.approvals.pending_expenses} Expenses (Rs.{(mgrData.approvals.pending_expense_total || 0).toLocaleString()})
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Org KPIs Row 1 */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <StatCard label="Employees" value={analytics.employees.total} sublabel={`${analytics.employees.active} active`} icon={Users} iconBg="bg-blue-100" iconColor="text-brand" testId="stat-employees" onClick={() => navigate('/employees')} accent="blue" />
                        <StatCard label="Present Today" value={`${mgrData.team.present_today}/${mgrData.team.total_employees}`} sublabel={`${mgrData.team.attendance_rate}% rate`} icon={Clock} iconBg="bg-green-100" iconColor="text-green-600" testId="stat-attendance" onClick={() => navigate('/geo-tracking')} accent="green" />
                        <StatCard label="Monthly Payroll" value={`Rs.${(analytics.payroll.current_month.total_net || 0).toLocaleString()}`} sublabel="net salary" icon={DollarSign} iconBg="bg-emerald-100" iconColor="text-emerald-600" testId="stat-payroll" onClick={() => navigate('/payroll')} accent="teal" />
                        <StatCard label="Pending Leaves" value={mgrData.approvals.pending_leaves} sublabel="awaiting approval" icon={Calendar} iconBg="bg-amber-100" iconColor="text-amber-600" testId="stat-leaves" onClick={() => navigate('/leaves')} accent="amber" />
                    </div>

                    {/* Org KPIs Row 2 */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <StatCard label="Tasks In Progress" value={mgrData.tasks.in_progress} sublabel={`${mgrData.tasks.overdue} overdue`} icon={ListTodo} iconBg="bg-indigo-100" iconColor="text-indigo-600" testId="stat-tasks" onClick={() => navigate('/tasks')} accent="indigo" />
                        <StatCard label="Avg Rating" value={mgrData.avg_rating || '-'} sublabel="team performance" icon={Star} iconBg="bg-amber-100" iconColor="text-amber-500" testId="stat-rating" onClick={() => navigate('/performance')} accent="purple" />
                        <StatCard label="Active Onboarding" value={mgrData.onboarding_active} sublabel="in progress" icon={ClipboardList} iconBg="bg-teal-100" iconColor="text-teal-600" testId="stat-onboarding" onClick={() => navigate('/onboarding')} accent="cyan" />
                        <StatCard label="Field Visits Today" value={`${mgrData.visits.completed}/${mgrData.visits.today}`} sublabel="completed" icon={MapPin} iconBg="bg-rose-100" iconColor="text-rose-600" testId="stat-visits" onClick={() => navigate('/visits')} accent="rose" />
                    </div>

                    {/* Charts + Pending Items */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Payroll Trend */}
                        <Card className="rounded-none border-slate-200" data-testid="payroll-chart">
                            <CardHeader className="border-b border-slate-200 py-3">
                                <CardTitle className="text-sm font-bold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" />Payroll Trend</CardTitle>
                            </CardHeader>
                            <CardContent className="p-4">
                                {analytics.payroll.trend.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={analytics.payroll.trend.map(item => ({ name: `${item._id.month}/${item._id.year}`, gross: item.total_gross, net: item.total_net }))}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#64748b" />
                                            <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                                            <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0 }} />
                                            <Bar dataKey="gross" fill="#94a3b8" name="Gross" />
                                            <Bar dataKey="net" fill="#2563eb" name="Net" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : <div className="flex items-center justify-center h-[250px] text-slate-500 text-sm">No payroll data</div>}
                            </CardContent>
                        </Card>

                        {/* Department Distribution */}
                        <Card className="rounded-none border-slate-200" data-testid="dept-chart">
                            <CardHeader className="border-b border-slate-200 py-3">
                                <CardTitle className="text-sm font-bold flex items-center gap-2"><Users className="h-4 w-4 text-brand" />Departments</CardTitle>
                            </CardHeader>
                            <CardContent className="p-4">
                                {analytics.department_distribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie data={analytics.department_distribution.map(i => ({ name: i._id, value: i.count }))} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} outerRadius={90} dataKey="value">
                                                {analytics.department_distribution.map((dept, i) => <Cell key={dept._id || `dept-${i}`} fill={COLORS[i % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : <div className="flex items-center justify-center h-[250px] text-slate-500 text-sm">No data</div>}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Pending Items Tables */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {mgrData.recent_leaves?.length > 0 && (
                            <Card className="rounded-none border-slate-200">
                                <CardHeader className="border-b border-slate-200 py-3">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm font-bold flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-500" />Pending Leaves</CardTitle>
                                        <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => navigate('/leaves')}>View All</Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {mgrData.recent_leaves.map(l => (
                                        <div key={l._id} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0">
                                            <div><p className="text-sm font-medium text-slate-900">{l.employee_name}</p><p className="text-[10px] text-slate-400">{l.leave_type} | {l.start_date?.split('T')[0]}</p></div>
                                            <Badge className="rounded-none bg-amber-100 text-amber-800 text-[10px]">pending</Badge>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                        {mgrData.recent_expenses?.length > 0 && (
                            <Card className="rounded-none border-slate-200">
                                <CardHeader className="border-b border-slate-200 py-3">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm font-bold flex items-center gap-2"><Receipt className="h-4 w-4 text-amber-500" />Pending Expenses</CardTitle>
                                        <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => navigate('/expenses')}>View All</Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {mgrData.recent_expenses.map(e => (
                                        <div key={e._id} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0">
                                            <div><p className="text-sm font-medium text-slate-900">{e.title}</p><p className="text-[10px] text-slate-400">{e.employee_name} | {e.category}</p></div>
                                            <span className="text-sm font-bold text-slate-900">Rs.{e.amount?.toLocaleString()}</span>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                        <QuickLink icon={FolderKanban} label="Projects" href="/projects" color="bg-indigo-100" />
                        <QuickLink icon={Package} label="Assets" href="/assets" color="bg-orange-100" />
                        <QuickLink icon={FileSpreadsheet} label="Reports" href="/reports" color="bg-cyan-100" />
                        <QuickLink icon={Target} label="Performance" href="/performance" color="bg-purple-100" />
                        <QuickLink icon={Wallet} label="Loans" href="/loans" color="bg-red-100" />
                        <QuickLink icon={Users} label="Employees" href="/employees" color="bg-blue-100" />
                    </div>
                </>
            )}

            {/* Manager-only: Also show personal stats */}
            {isManager && myData && (
                <Card className="rounded-none border-slate-200">
                    <CardHeader className="border-b border-slate-200 py-3">
                        <CardTitle className="text-sm font-bold">My Personal Stats</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="grid grid-cols-4 gap-4 text-center">
                            <div><p className="text-xs text-slate-500">My Tasks</p><p className="text-lg font-bold text-slate-900">{myData.tasks.pending}</p></div>
                            <div><p className="text-xs text-slate-500">My Leaves</p><p className="text-lg font-bold text-slate-900">{myData.leaves.pending}</p></div>
                            <div><p className="text-xs text-slate-500">My Expenses</p><p className="text-lg font-bold text-slate-900">{myData.expenses.pending}</p></div>
                            <div><p className="text-xs text-slate-500">Goals Done</p><p className="text-lg font-bold text-green-600">{myData.goals.completed}/{myData.goals.total}</p></div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default Dashboard;
